import {
  NotificationBridge,
  type NotificationPermissionState,
  type ScheduledReminder,
  type ShowNotificationOptions,
} from "@cartero/shared";

import type { NotificationAdapter } from "./types";

/**
 * Web / installed-PWA adapter. Surfaces foreground notifications through the
 * shared `NotificationBridge` (Service Worker first, constructor fallback).
 *
 * The web platform has no push backend here, so it cannot register reminders
 * that fire while the app is closed — `supportsScheduling` is `false` and the
 * reminder hook falls back to foreground polling. The scheduling methods are
 * inert no-ops so the adapter still satisfies the shared interface.
 */
export class WebNotificationAdapter implements NotificationAdapter {
  readonly supportsScheduling = false;
  private readonly bridge = new NotificationBridge({ isWeb: true });

  async getPermission(): Promise<NotificationPermissionState> {
    return this.bridge.getPermission();
  }

  async requestPermission(): Promise<boolean> {
    return this.bridge.requestPermission();
  }

  async show(title: string, options?: ShowNotificationOptions): Promise<void> {
    await this.bridge.show(title, options);
  }

  async getScheduled(): Promise<ScheduledReminder[]> {
    return [];
  }

  async schedule(): Promise<void> {
    // No OS scheduling on the web — handled by foreground polling instead.
  }

  async cancel(): Promise<void> {
    // No OS-scheduled reminders exist on the web — nothing to cancel.
  }

  async cancelAll(): Promise<void> {
    // No OS-scheduled reminders exist on the web — nothing to cancel.
  }
}
